import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { SentenceType } from '../../../../server/agent/learningContent/learningContentTypes';
import { ChapterDetailResponse } from '../../../../server/response/listeningResponseScheme';
import { ChapterStatus } from '../../../../types/listeningTypes';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import { useLessonStore } from './LessonProvider';

const CHAPTER_STATUS_POLLING_INTERVAL = 7000;

const CHAPTER_THINKING_TEXT_MESSAGES = [
    '학습 자료를 만들고 있어요...',
    '내용을 분석하고 있습니다...',
    '1~5분 정도 걸릴 수 있어요...',
    '문장을 조합하고 있습니다...',
    '문맥을 파악하고 있어요...',
    '번역을 생성할께요...',
    '자연스럽게 만드는 중...',
    '핵심 표현을 추출합니다...',
    '학습 내용을 정리하고 있어요...',
    '이제 거의 완료됐어요...',
    '조금만 더 기다려주세요...',
    '앗, 잠시 뒤에 새로고침 해보세요.',
];

/**
 * 챕터 상세 정보 관리
 * - 챕터 상세 데이터
 * - 문장 목록
 * - 현재 문장 인덱스 (0부터 시작)
 * - 챕터 생성 상태 polling
 * - 자동 재생 모드 (복습용)
 */
export class ChapterStore {
    readonly lessonHash: string;
    readonly chapterHash: string;

    chapter: ChapterDetailResponse | null = null;

    isLoading = true;

    // 배열 인덱스 (0부터 시작)
    currentSentenceIndex: number = 0;

    // LessonStore에 알리기 위한 콜백
    private onSentenceChange?: (sentenceNum: number) => void;

    // Polling 관련
    pollingTimeout: NodeJS.Timeout | null = null;
    pollingCount: number = 0;

    // 자동 재생 상태
    isAutoPlaying: boolean = false;

    // 문장 재생 중 여부
    isSentencePlaying: boolean = false;

    // 현재 재생 시간 (진행 막대용)
    currentPlaybackTime: number = 0;

    constructor(
        lessonHash: string,
        chapterHash: string,
        onSentenceChange?: (sentenceNum: number) => void,
    ) {
        makeAutoObservable(this);

        this.lessonHash = lessonHash;
        this.chapterHash = chapterHash;
        this.onSentenceChange = onSentenceChange;
    }

    /**
     * 챕터 상태
     */
    get chapterStatus(): ChapterStatus | null {
        return (this.chapter?.status as ChapterStatus) || null;
    }

    get chapterStatusUpdatedAt(): string | null {
        return this.chapter?.statusUpdatedAt || null;
    }

    /**
     * 챕터 생성 오류 여부
     */
    get hasChapterError(): boolean {
        return !!this.chapter?.hasError;
    }

    /**
     * 챕터 생성 중 여부
     */
    get isCreatingChapter(): boolean {
        return (
            !this.hasChapterError &&
            !!this.chapterStatus &&
            this.chapterStatus !== 'none' &&
            this.chapterStatus !== 'completed'
        );
    }

    /**
     * 챕터 생성 중 표시할 메시지
     */
    get chapterThinkingText(): string {
        return CHAPTER_THINKING_TEXT_MESSAGES[this.pollingCount] || '';
    }

    /**
     * 마지막 thinking text 여부
     */
    get isLastChapterThinkingText(): boolean {
        return this.pollingCount >= CHAPTER_THINKING_TEXT_MESSAGES.length - 1;
    }

    /**
     * 챕터의 문장 목록
     */
    get learningContent(): SentenceType[] {
        return this.chapter?.learningContent || [];
    }

    get sentenceCount(): number {
        return this.learningContent.length;
    }

    /**
     * 현재 문장 데이터
     */
    get currentSentence(): SentenceType | null {
        return this.learningContent[this.currentSentenceIndex] || null;
    }

    /**
     * 현재 문장 번호 (1부터 시작)
     */
    get currentSentenceNum(): number {
        return this.currentSentence?.num || 1;
    }

    /**
     * 첫 번째 문장 여부
     */
    get isFirstSentence(): boolean {
        return this.currentSentenceIndex === 0;
    }

    /**
     * 마지막 문장 여부
     */
    get isLastSentence(): boolean {
        return (
            this.learningContent.length > 0 &&
            this.currentSentenceIndex === this.learningContent.length - 1
        );
    }

    /**
     * 챕터 시작 시간 (첫 문장의 시작 시간)
     */
    get chapterStartTime(): number {
        const firstSentence = this.learningContent[0];
        return firstSentence?.startTime ?? 0;
    }

    /**
     * 챕터 종료 시간 (마지막 문장의 종료 시간)
     */
    get chapterEndTime(): number {
        const lastSentence = this.learningContent[this.learningContent.length - 1];
        return lastSentence?.endTime ?? 0;
    }

    /**
     * 챕터 상세 데이터 로드
     */
    async fetchChapterDetail() {
        this.isLoading = true;

        try {
            const chapter = await apiClient.chapter.member.getChapterDetail.$query({
                lessonHash: this.lessonHash,
                chapterHash: this.chapterHash,
            });

            runInAction(() => {
                this.chapter = chapter;
            });

            // 상세 정보를 가져왔는데 아직 챕터 생성 중이면 상태 polling 시작
            if (this.isCreatingChapter) {
                this.startChapterStatusPolling();
            }
        } catch {
            // ignore
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * 챕터 상태 polling 시작
     */
    startChapterStatusPolling() {
        this.clearPollingTimeout();

        this.pollingTimeout = setTimeout(() => {
            this.fetchChapterStatusPolling();
        }, CHAPTER_STATUS_POLLING_INTERVAL);
    }

    /**
     * 챕터 상태 polling
     */
    async fetchChapterStatusPolling() {
        this.clearPollingTimeout();

        const statusResponse = await apiClient.chapter.public.getChapterStatus.$forceQuery({
            chapterHash: this.chapterHash,
        });

        if (!statusResponse) {
            return;
        }

        const { status, statusUpdatedAt, hasError } = statusResponse;

        runInAction(async () => {
            // 오류가 발생했으면 polling 종료하고 재시도 버튼 표시
            if (hasError) {
                runInAction(() => {
                    this.chapter = {
                        ...this.chapter!,

                        status,
                        statusUpdatedAt,
                        hasError: true,
                    };
                });
                return;
            } else if (status === 'completed') {
                // 완료되었으면 챕터 상세 데이터를 다시 가져온다
                const chapter = await apiClient.chapter.member.getChapterDetail.$forceQuery({
                    lessonHash: this.lessonHash,
                    chapterHash: this.chapterHash,
                });

                runInAction(() => {
                    this.chapter = chapter;
                });
            } else {
                // 아직 진행 중이면 계속 폴링
                this.pollingCount++;

                if (this.isLastChapterThinkingText) {
                    // 마지막 메시지인 경우 polling을 종료한다
                    this.clearPollingTimeout();
                } else {
                    this.startChapterStatusPolling();
                }
            }
        });
    }

    /**
     * Polling timeout 정리
     */
    clearPollingTimeout() {
        if (this.pollingTimeout) {
            clearTimeout(this.pollingTimeout);
        }
    }

    /**
     * 문장 인덱스 변경
     */
    setCurrentSentenceIndex(index: number) {
        if (index < 0 || index >= this.learningContent.length) {
            console.warn(
                `Invalid sentenceIndex: ${index}, valid range: 0~${this.learningContent.length - 1}`,
            );
            return;
        }

        this.currentSentenceIndex = index;

        // LessonStore에 알림
        const sentenceNum = this.learningContent[index]?.num;
        if (sentenceNum) {
            this.onSentenceChange?.(sentenceNum);
        }
    }

    /**
     * sentenceNum으로 문장 설정 (1부터 시작)
     */
    setSentenceByNum(sentenceNum: number) {
        if (this.learningContent.length === 0) {
            return;
        }

        const index = this.learningContent.findIndex((s) => s.num === sentenceNum);

        if (index !== -1) {
            this.currentSentenceIndex = index;
        } else {
            // 범위를 벗어나면 첫 문장으로
            console.warn(`Sentence ${sentenceNum} not found, defaulting to first sentence`);
            this.currentSentenceIndex = 0;
        }
    }

    /**
     * 문장을 이동하고 이벤트 콜백을 발생
     * - 이벤트 발생하지 않으려면 setSentenceByNum 사용
     */
    moveToSentence(sentenceNum: number) {
        const previousIndex = this.currentSentenceIndex;

        this.setSentenceByNum(sentenceNum);

        if (this.currentSentenceIndex !== previousIndex) {
            this.onSentenceChange?.(sentenceNum);
        }
    }

    /**
     * 다음 문장
     * - 현재 문장 완료 처리 후 다음 문장으로 이동
     */
    moveToNextSentence() {
        const completedSentenceNum = this.currentSentenceNum;

        // 문장 완료 처리 (진도율 기록 + 다음 챕터 선행 생성)
        // 응답을 기다리지는 않음
        apiClient.chapter.member.completeSentence.mutate({
            lessonHash: this.lessonHash,
            chapterHash: this.chapterHash,
            sentenceNum: completedSentenceNum,
        });

        if (!this.isLastSentence) {
            this.moveToSentence(this.currentSentenceNum + 1);
        }
    }

    /**
     * 이전 문장
     */
    moveToPrevSentence() {
        if (!this.isFirstSentence) {
            this.moveToSentence(this.currentSentenceNum - 1);
        }
    }

    /**
     * 챕터 학습자료 생성 재시도
     */
    async retryCreateChapter() {
        this.pollingCount = 0;

        const updatedChapterStatus = await apiClient.chapter.member.retryCreateChapter.mutate({
            lessonHash: this.lessonHash,
            chapterHash: this.chapterHash,
        });

        runInAction(() => {
            this.chapter = {
                ...this.chapter!,

                status: updatedChapterStatus.status,
                statusUpdatedAt: updatedChapterStatus.statusUpdatedAt,
                hasError: updatedChapterStatus.hasError,
            };
        });

        // updatedChapter 상태가 업데이트 되었으면 다시 폴링
        if (this.isCreatingChapter) {
            this.startChapterStatusPolling();
        }
    }

    /**
     * 자동 재생 시작
     * - 복습 모드로 영상과 함께 문장이 자동으로 전환됨
     */
    startAutoPlay() {
        this.isAutoPlaying = true;
        // auto play 시작 시 현재 시간을 0으로 초기화해준다
        // - 마지막 챕터의 영상 재생이 끝날 때 챕터 길이보다 더 큰 값이 할당될 수도 있다
        //   currentPlaybackTime 값은 타이머 때문에 더 뒤의 값으로 업데이트될 수 있기 때문이다
        //   이것 때문에 간헐적으로 챕터 재생이 안되는 문제가 있기도 했다
        // - 챕터 영상 전체 보기는 ChapterCompleteModalContent 참고
        this.currentPlaybackTime = 0;
        this.setSentenceByNum(1); // 첫 문장으로 이동
    }

    /**
     * 자동 재생 종료
     */
    stopAutoPlay() {
        this.isAutoPlaying = false;
    }

    /**
     * 문장 재생 시작
     * - 플래그처리만 하고 실제 재생은 PlaybackControl 에서 처리
     * - 재생 종료 후 멈췄을 때 stopSentencePlaying 를 호출하는데, 이건 PlaybackBar 에서 처리
     * - 문장 재생과 영상을 직접 재생한 것을 구분하기 위함
     */
    startSentencePlaying() {
        this.isSentencePlaying = true;
    }

    stopSentencePlaying() {
        this.isSentencePlaying = false;
    }

    /**
     * 현재 재생 시간의 문장으로 이동하기
     */
    moveToCurrentPlaybackTimeSentence() {
        // 현재 문장 범위 내면 현재 인덱스 유지
        const current = this.currentSentence;
        if (
            current &&
            this.currentPlaybackTime >= current.startTime &&
            this.currentPlaybackTime < current.endTime
        ) {
            return;
        }

        // 다른 문장 탐색
        const sentence = this.learningContent.find(
            (s) => this.currentPlaybackTime >= s.startTime && this.currentPlaybackTime < s.endTime,
        );

        if (sentence) {
            this.moveToSentence(sentence.num);
        }
    }

    /**
     * 시간 업데이트 처리
     * - 항상 현재 재생 시간 업데이트 (진행 막대용)
     * - 자동 재생 모드일 때만 문장 자동 전환
     */
    handleTimeUpdate(currentTime: number) {
        // 항상 현재 재생 시간 업데이트 (진행 막대용)
        this.currentPlaybackTime = currentTime;
    }
}

const ChapterContext = createContext<ChapterStore | null>(null);

export const ChapterProvider = observer(
    ({
        lessonHash,
        chapterHash,
        sentenceNum = 1,
        onSentenceChange,
        children,
    }: {
        lessonHash: string;
        chapterHash: string;
        sentenceNum?: number;
        onSentenceChange?: (sentenceNum: number) => void;
        children: ReactNode;
    }) => {
        const lessonStore = useLessonStore();
        const chapterStore = useMobxInstance(
            ChapterStore,
            lessonHash,
            chapterHash,
            onSentenceChange,
        );

        // 챕터 데이터 로드
        useEffect(() => {
            if (chapterStore.chapterHash) {
                chapterStore.fetchChapterDetail();
            }

            return () => {
                chapterStore.clearPollingTimeout();
            };
        }, [chapterStore]);

        // 챕터 데이터 로드 완료 후 sentenceNum 적용
        useEffect(() => {
            if (chapterStore.chapter && !chapterStore.isLoading) {
                chapterStore.setSentenceByNum(sentenceNum);
            }
        }, [chapterStore, chapterStore.chapter, chapterStore.isLoading, sentenceNum]);

        // 시간 업데이트 콜백 등록 (진행 막대 + 자동 재생용)
        useEffect(() => {
            lessonStore.setOnTimeUpdateCallback((currentTime) => {
                chapterStore.handleTimeUpdate(currentTime);
            });

            return () => {
                lessonStore.setOnTimeUpdateCallback(null);

                if (chapterStore.isAutoPlaying) {
                    chapterStore.stopAutoPlay();
                    lessonStore?.lessonPlayerRef?.current?.pause();
                }
            };
        }, [lessonStore, chapterStore]);

        return <ChapterContext.Provider value={chapterStore}>{children}</ChapterContext.Provider>;
    },
);

/**
 * 챕터 스토어 사용 훅
 */
export const useChapterStore = (): ChapterStore => {
    const chapterStore = useContext(ChapterContext);

    if (!chapterStore) {
        throw new Error('useChapterStore must be used within a ChapterProvider');
    }

    return chapterStore;
};
